/*
 * Product Showcase scroll controller.
 *
 * Sections render in normal document flow. On desktop we make the featured image
 * behave as if it were pinned across sibling sections (which a plain position:sticky
 * on the in-flow image can't do, because each section is wrapped in its own
 * .shopify-section):
 *
 *   1. Wrap each run of directly-adjacent showcase sections in a relative
 *      .product-showcase-stage (content is NOT moved — the wrapper is only an
 *      offset parent that spans the whole run).
 *   2. Clone every section's image into ONE layer, stacked on top of each other,
 *      and hide the in-flow images. The layer sits in a .product-showcase-stage__rail
 *      — an absolutely positioned, full-height column over the media side — and is
 *      itself position:sticky, so the browser (not JS) pins it across the whole run
 *      and parks it at the bottom. Nothing about the pin is scroll-linked, so it
 *      can never lag a frame behind the scroll.
 *   3. Once most of a section has entered the viewport its image is switched on
 *      and fades in over the previous one (CSS transition, not scroll-linked).
 *
 * On mobile the stage is inactive: images stay in flow and scroll normally.
 */
(function () {
  'use strict';

  // Each section renders its own <script src> tag for this file, so the browser
  // executes it once per section on the page. Only the first run may boot.
  if (window.kneskoProductShowcase) return;

  var DESKTOP = '(min-width: 750px)';

  // How much of a section must have entered the viewport before its image is
  // switched on (0–1).
  var ENTER_THRESHOLD = 0.5;

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  function wrapperOf(el) {
    return el.closest('.shopify-section') || el;
  }

  function ShowcaseController() {
    this.stages = [];
    this.ticking = false;
    this.update = this.update.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.rebuild = this.rebuild.bind(this);
  }

  ShowcaseController.prototype.collectRuns = function () {
    var sections = Array.prototype.slice.call(
      document.querySelectorAll('[data-product-showcase]')
    );
    var runs = [];
    var run = [];
    for (var i = 0; i < sections.length; i++) {
      if (!run.length) {
        run.push(sections[i]);
      } else if (
        wrapperOf(run[run.length - 1]).nextElementSibling ===
          wrapperOf(sections[i]) &&
        !sections[i].hasAttribute('data-showcase-group-start')
      ) {
        run.push(sections[i]);
      } else {
        runs.push(run);
        run = [sections[i]];
      }
    }
    if (run.length) runs.push(run);
    return runs;
  };

  ShowcaseController.prototype.buildStage = function (sections) {
    var firstWrapper = wrapperOf(sections[0]);
    var parent = firstWrapper.parentNode;

    var stage = document.createElement('div');
    stage.className = 'product-showcase-stage';
    parent.insertBefore(stage, firstWrapper);

    // Move the section wrappers into the stage (content stays intact).
    for (var i = 0; i < sections.length; i++) {
      stage.appendChild(wrapperOf(sections[i]));
    }

    // Build the stacked image layer inside a full-height rail. The rail spans the
    // stage so the sticky layer inside it pins for exactly the run's scroll length.
    var rail = document.createElement('div');
    rail.className = 'product-showcase-stage__rail';
    rail.setAttribute('aria-hidden', 'true');

    var layer = document.createElement('div');
    layer.className = 'product-showcase-stage__media';
    rail.appendChild(layer);

    var mediaEls = [];
    for (var j = 0; j < sections.length; j++) {
      var media = sections[j].querySelector('[data-showcase-media]');
      if (!media) continue;
      var clone = media.cloneNode(true);
      layer.appendChild(clone);
      mediaEls.push(clone);
    }
    if (mediaEls.length) mediaEls[0].style.opacity = 1;
    stage.insertBefore(rail, stage.firstChild);

    this.stages.push({
      stage: stage,
      rail: rail,
      mediaEls: mediaEls,
      sections: sections,
      mediaCol: sections[0].querySelector('.product-showcase__media-col'),
    });
  };

  ShowcaseController.prototype.build = function () {
    var runs = this.collectRuns();
    for (var i = 0; i < runs.length; i++) this.buildStage(runs[i]);
    if (this.stages.length) {
      this.layout();
      this.update();
    }
  };

  ShowcaseController.prototype.teardown = function () {
    for (var s = 0; s < this.stages.length; s++) {
      var st = this.stages[s];
      var parent = st.stage.parentNode;
      // Move the section wrappers back out, restoring original order.
      for (var i = 0; i < st.sections.length; i++) {
        parent.insertBefore(wrapperOf(st.sections[i]), st.stage);
      }
      st.stage.remove();
    }
    this.stages = [];
  };

  ShowcaseController.prototype.rebuild = function () {
    this.teardown();
    this.build();
  };

  ShowcaseController.prototype.init = function () {
    if (!document.querySelector('[data-product-showcase]')) return;
    this.build();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('shopify:section:load', this.rebuild);
    document.addEventListener('shopify:section:unload', this.rebuild);
  };

  ShowcaseController.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  };

  ShowcaseController.prototype.onResize = function () {
    this.layout();
    this.onScroll();
  };

  // Align each rail with its stage's media column. Geometry only — this runs on
  // build and resize, never per scroll frame.
  ShowcaseController.prototype.layout = function () {
    var isDesktop = window.matchMedia(DESKTOP).matches;

    for (var s = 0; s < this.stages.length; s++) {
      var st = this.stages[s];

      if (!isDesktop || !st.rail || !st.mediaCol) {
        st.stage.classList.remove('is-active');
        continue;
      }
      st.stage.classList.add('is-active');

      var stageRect = st.stage.getBoundingClientRect();
      var colRect = st.mediaCol.getBoundingClientRect();
      st.rail.style.width = colRect.width + 'px';
      st.rail.style.left = colRect.left - stageRect.left + 'px';
    }
  };

  ShowcaseController.prototype.update = function () {
    this.ticking = false;

    var vh = window.innerHeight || document.documentElement.clientHeight;

    for (var s = 0; s < this.stages.length; s++) {
      var st = this.stages[s];
      if (!st.stage.classList.contains('is-active')) continue;

      // Switch each image on once most of its section has entered the viewport.
      // Opacity is binary (0/1) — the CSS transition does the fading, so the
      // images don't fade in and out gradually while scrolling.
      for (var i = 1; i < st.mediaEls.length; i++) {
        var rect = st.sections[i].getBoundingClientRect();
        // Fraction of the section that has scrolled into the viewport. Sections
        // taller than the viewport are measured against the viewport instead, so
        // they can still reach 1.
        var span = Math.min(rect.height, vh) || 1;
        var entered = clamp((vh - rect.top) / span, 0, 1);
        st.mediaEls[i].style.opacity = entered >= ENTER_THRESHOLD ? 1 : 0;
      }
    }
  };

  function boot() {
    window.kneskoProductShowcase = new ShowcaseController();
    window.kneskoProductShowcase.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
